import { FC, useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import ru from "date-fns/locale/ru";
import moment from "moment";
import clsx from "clsx";

import {
  actionGetTablesUnloadingsFilters,
  actionDeleteTablesUnloadings,
  actionGetTablesUnloadings,
} from "../../../redux/action/unloadings/actionTablesUnloadings";
import {
  formatUnloadingsDate,
  formatUnloadingsTime,
} from "../../../utils/formatDate";
import {
  reducerTablesUnloadingsInputValues,
  reducerTablesUploadingStatus,
} from "../../../redux/reducer/unloadings/reducers/tablesUnloadinds";
import { createOptions } from "../../../utils/createOptions";
import { generateStyles } from "../../../utils/generateToastStyles";
import { AppDispatch } from "../../../redux/reducer/store";

import LimitSelect from "../../custom/limitSelect/limitSelect";
import Pagination from "../../pagination/pagination";
import Select from "../../custom/select/select";
import Loader from "../../custom/loader/loader";
import ToastPopup from "../../custom/toastPopup/toastPopup";

import RefreshIcon from "../../../assets/icons/refresh.svg";

import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

interface IUnloadings {
  date: string;
  employee: string;
  order: { id: number };
  payroll: { id: number };
  status: { [key: string]: string };
  time: string;
  type: string;
}

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

const statusesOptions = [
  { value: "", label: "Все" },
  { value: "Ordered", label: "В очереди" },
  { value: "Error", label: "Ошибка" },
  { value: "Done", label: "Сформирован" },
  { value: "Deleted", label: "Удален" },
  { value: "Empty", label: "Фото не найдено" },
];

const timeOptions = [
  { value: "", label: "Все" },
  { value: "1", label: "Больше минуты" },
  { value: "10", label: "Больше 10 минут" },
];

const TableAdminUnloadingTables: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    tablesUnloadingsInputValues,
    tablesUnloadingsCount,
    tablesUnloadingsStatus,
    tablesUnloadings,
    filters,
    limit,
    page,
  } = useSelector((state: any) => state.unloadings.tablesUnloadings);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );

  const [selectedDate, setSelectedDate] = useState<Date | null>(
    tablesUnloadingsInputValues.date &&
      new Date(tablesUnloadingsInputValues.date)
  );
  const [selectedStatusOption, setSelectedStatusOption] = useState<string>("");
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);

  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  const requestStatusOptions = [
    {
      status: 0,
      errorMessage: "Ошибка на стороне сервера.",
    },
  ];

  useEffect(() => {
    setIsPending(true);
    dispatch(actionGetTablesUnloadings(actualLimit, page)).then(() => {
      setIsPending(false);
    });
    dispatch(actionGetTablesUnloadingsFilters());
    registerLocale("ru", ru);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const value = event.value;
    if (value === "") {
      if (name === "status") {
        setSelectedStatusOption("all");
      }

      const updatedInputValues = { ...tablesUnloadingsInputValues };
      delete updatedInputValues[name];

      dispatch(reducerTablesUnloadingsInputValues(updatedInputValues));
    } else {
      dispatch(
        reducerTablesUnloadingsInputValues({
          ...tablesUnloadingsInputValues,
          [name]:
            name === "moreThanMin"
              ? parseFloat(value)
              : name === "employee"
              ? { id: value }
              : value,
        })
      );
    }

    setIsPending(true);
    dispatch(actionGetTablesUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
    resetPages();
  };

  const handleDatePickerChange = (date: Date) => {
    setSelectedDate(date);

    const formattedDate = moment(date).format("YYYY-MM-DD");

    dispatch(
      reducerTablesUnloadingsInputValues({
        ...tablesUnloadingsInputValues,
        date: formattedDate,
      })
    );

    setIsPending(true);
    dispatch(actionGetTablesUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleClearDate = () => {
    if (selectedDate) {
      setSelectedDate(null);

      dispatch(
        reducerTablesUnloadingsInputValues({
          ...tablesUnloadingsInputValues,
          date: "",
        })
      );

      setIsPending(true);
      dispatch(actionGetTablesUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    }
  };

  const resetPages = () => {
    setCurrentPage(1);
    setStartPage(1);
  };

  const handleChange = (newLimit: number, newPage: number) => {
    setIsPending(true);
    dispatch(actionGetTablesUnloadings(newLimit, newPage)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 1);
    resetPages();
  };

  const handleClickRefreshBtn = () => {
    setIsPending(true);
    dispatch(actionGetTablesUnloadings(limit, page)).then(() => {
      setIsPending(false);
    });
  };

  const handleClickDeleteBtn = (id: number) => {
    setIsPending(true);
    dispatch(actionDeleteTablesUnloadings(id)).then(() => {
      dispatch(actionGetTablesUnloadings(limit, page)).then(() => {
        setIsPending(false);
      });
    });
  };

  useEffect(() => {
    requestStatusOptions.forEach((option: any) => {
      if (tablesUnloadingsStatus === option.status) {
        toast.dismiss();

        toast(
          (t) => (
            <ToastPopup
              title={
                option.successMessage
                  ? option.successMessage
                  : option.errorMessage
              }
              isSuccess={option.successMessage ? true : false}
              toast={toast}
              t={t}
            />
          ),
          generateStyles(option.successMessage ? true : false)
        );
      }
    });

    setTimeout(() => {
      dispatch(reducerTablesUploadingStatus(null));
    }, 5000);
    // eslint-disable-next-line
  }, [dispatch, tablesUnloadingsStatus]);

  return (
    <>
      <div className="loader__wrapper">
        {(!tablesUnloadings || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        {tablesUnloadingsStatus && tablesUnloadingsStatus !== null && (
          <Toaster position="top-center" toastOptions={{ duration: 1500 }} />
        )}

        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__data_picker filter__data_picker_excel">
              <DatePicker
                onChange={handleDatePickerChange}
                onKeyDown={handleDateKeyDown}
                selected={selectedDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Дата"
                autoComplete="off"
                locale="ru"
                name="date"
              />
              {selectedDate && (
                <div className="clear__icon" onClick={handleClearDate} />
              )}
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  tablesUnloadingsInputValues.employee &&
                  createOptions(filters, "employees")?.find(
                    (option: { value: string; label: string }) =>
                      option.value === tablesUnloadingsInputValues.employee.id
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "employee")
                }
                isShowPlaceholder={
                  (tablesUnloadingsInputValues?.employee &&
                    !!Object.keys(tablesUnloadingsInputValues?.employee)
                      .length) ||
                  tablesUnloadingsInputValues?.employee === undefined
                }
                options={createOptions(filters, "employees")}
                className="users__filter"
                placeholder="Сотрудник"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  tablesUnloadingsInputValues.status &&
                  statusesOptions.find(
                    (option: { value: string; label: string }) =>
                      option.value === tablesUnloadingsInputValues.status
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  selectChange(event, "status")
                }
                isShowPlaceholder={
                  !!tablesUnloadingsInputValues.status ||
                  selectedStatusOption === "all"
                }
                options={statusesOptions}
                className="users__filter"
                placeholder="Статус"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  tablesUnloadingsInputValues.moreThanMin &&
                  timeOptions.find(
                    (option: { value: string; label: string }) =>
                      parseFloat(option.value) ===
                      tablesUnloadingsInputValues.moreThanMin
                  )
                }
                propsChange={(event: { value: string; label: string }) => {
                  selectChange(event, "moreThanMin");
                }}
                isShowPlaceholder={
                  !!tablesUnloadingsInputValues.moreThanMin ||
                  tablesUnloadingsInputValues.moreThanMin === undefined
                }
                placeholder="Время выполнения"
                className="users__filter"
                options={timeOptions}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="refresh__btn" onClick={handleClickRefreshBtn}>
              <img className="refresh__icon" src={RefreshIcon} alt="refresh" />
            </div>
          </div>
        </div>

        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr className="unloadings__filter_tr">
                <th className="reports__number_th">№</th>
                <th>
                  <div className="filter__data_picker filter__data_picker_excel">
                    <DatePicker
                      onChange={handleDatePickerChange}
                      onKeyDown={handleDateKeyDown}
                      selected={selectedDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Дата"
                      autoComplete="off"
                      locale="ru"
                      name="date"
                    />
                    {selectedDate && (
                      <div className="clear__icon" onClick={handleClearDate} />
                    )}
                  </div>
                </th>
                <th className="filter__block">Название</th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        tablesUnloadingsInputValues.employee &&
                        createOptions(filters, "employees")?.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            tablesUnloadingsInputValues.employee.id
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "employee")
                      }
                      isShowPlaceholder={
                        (tablesUnloadingsInputValues?.employee &&
                          !!Object.keys(tablesUnloadingsInputValues?.employee)
                            .length) ||
                        tablesUnloadingsInputValues?.employee === undefined
                      }
                      options={createOptions(filters, "employees")}
                      className="users__filter"
                      placeholder="Сотрудник"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        tablesUnloadingsInputValues.status &&
                        statusesOptions.find(
                          (option: { value: string; label: string }) =>
                            option.value === tablesUnloadingsInputValues.status
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        selectChange(event, "status")
                      }
                      isShowPlaceholder={
                        !!tablesUnloadingsInputValues.status ||
                        selectedStatusOption === "all"
                      }
                      options={statusesOptions}
                      className="users__filter"
                      placeholder="Статус"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        tablesUnloadingsInputValues.moreThanMin &&
                        timeOptions.find(
                          (option: { value: string; label: string }) =>
                            parseFloat(option.value) ===
                            tablesUnloadingsInputValues.moreThanMin
                        )
                      }
                      propsChange={(event: {
                        value: string;
                        label: string;
                      }) => {
                        selectChange(event, "moreThanMin");
                      }}
                      isShowPlaceholder={
                        !!tablesUnloadingsInputValues.moreThanMin ||
                        tablesUnloadingsInputValues.moreThanMin === undefined
                      }
                      placeholder="Время выполнения"
                      className="users__filter"
                      options={timeOptions}
                    />
                  </div>
                </th>
                <th className="unloadings__link_cell">Ссылка</th>
                <th>
                  <div className="refresh__btn" onClick={handleClickRefreshBtn}>
                    <img
                      className="refresh__icon"
                      src={RefreshIcon}
                      alt="refresh"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {tablesUnloadings &&
                tablesUnloadings.map((elem: IUnloadings, index: number) => (
                  <tr key={index}>
                    <td>{elem?.order?.id}</td>
                    <td>{formatUnloadingsDate(elem?.date)}</td>
                    <td>{elem?.type}</td>
                    <td>{elem?.employee}</td>
                    <td>{Object.values(elem.status)}</td>
                    <td>{formatUnloadingsTime(elem.time)}</td>
                    <td>
                      {Object.keys(elem.status).toString() === "Done" && (
                        <Link
                          to={`/finance/payroll/info/${elem.payroll.id}`}
                          className="report__link"
                        >
                          Перейти
                        </Link>
                      )}
                      {elem.status &&
                        Object.keys(elem.status).toString() === "Ordered" && (
                          <span
                            className="report__link"
                            onClick={() => handleClickDeleteBtn(elem.order.id)}
                          >
                            Отмена
                          </span>
                        )}
                    </td>
                    <td></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">
            Всего: {tablesUnloadingsCount}
          </div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            count={tablesUnloadingsCount}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
            pageName="userReports"
          />
        </div>
      </div>
    </>
  );
};

export default TableAdminUnloadingTables;
